import { useState } from 'react';
import { strengthsAPI, usersAPI } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { getLocalizedErrorMessage } from '@/lib/error-i18n';
import i18n from '@/lib/i18n';

interface StrengthRank {
  strengthName: string;
  rank: number;
}

interface SaveStrengthsData {
  username: string;
  strengths: StrengthRank[];
}

export const useStrengths = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const saveStrengthsRanks = async (data: SaveStrengthsData) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await strengthsAPI.saveRanks(data);

      if (response.success) {
        toast({
          title: i18n.t("common.success"),
          description: i18n.t("messages.strengthsSaved"),
        });
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to save strengths');
      }
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.saveStrengthsFailed');
      setError(errorMessage);
      
      toast({
        title: i18n.t("common.error"),
        description: errorMessage,
        variant: "destructive",
      });
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const getUserStrengths = async (userId?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await strengthsAPI.getUserStrengths(userId);

      if (response.success) {
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to fetch strengths');
      }
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.fetchStrengthsFailed');
      setError(errorMessage);
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const getAllStrengths = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await strengthsAPI.getAllStrengths();

      if (response.success) {
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to fetch all strengths');
      }
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.fetchStrengthsFailed');
      setError(errorMessage);
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const getAllUsersOverview = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await strengthsAPI.getAllUsersOverview();

      if (response.success) {
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to fetch users overview');
      }
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.fetchUsersFailed');
      setError(errorMessage);
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const getUserRanks = async (username: string, view?: 'top10' | 'bydomain') => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await strengthsAPI.getUserRanks(username, view);

      if (response.success) {
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to fetch user ranks');
      }
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.fetchRanksFailed');
      setError(errorMessage);
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const getMyTop10 = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await strengthsAPI.getMyTop10();

      if (response.success) {
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to fetch top 10 strengths');
      }
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.fetchTop10Failed');
      setError(errorMessage);
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const getMyByDomain = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await strengthsAPI.getMyByDomain();

      if (response.success) {
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to fetch strengths by domain');
      }
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.fetchByDomainFailed');
      setError(errorMessage);
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const updateUserEmail = async (username: string, email: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await strengthsAPI.bulkUpdateUserEmail(username, email);

      if (response.success) {
        toast({
          title: i18n.t("common.success"),
          description: i18n.t("messages.emailUpdated"),
        });
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to update email');
      }
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.updateEmailFailed');
      setError(errorMessage);
      
      toast({
        title: i18n.t("common.error"),
        description: errorMessage,
        variant: "destructive",
      });
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const getMyCliftonStrengths = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await usersAPI.getMyCliftonStrengths();

      if (response.success) {
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to fetch Clifton Strengths');
      }
    } catch (err: any) {
      const errorMessage = getLocalizedErrorMessage(err, 'errors.fetchStrengthsFailed');
      setError(errorMessage);

      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    error,
    saveStrengthsRanks,
    getUserStrengths,
    getAllStrengths,
    getAllUsersOverview,
    getUserRanks,
    getMyTop10,
    getMyByDomain,
    getMyCliftonStrengths,
    updateUserEmail,
  };
};
